import type { AppEntry } from '@/constants/apps'

import { appCategoryGroups, apps } from '@/constants/apps'

export type AppSort = 'popular' | 'recent' | 'alphabetical'

export const slugify = (value: string): string => value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

export const appAliases: Record<string, string> = {
    'quickbooks': 'QuickBooks Online',
    'qbo': 'QuickBooks Online',
    'dynamics': 'Microsoft Dynamics 365',
    'dynamics 365': 'Microsoft Dynamics 365',
    'outlook': 'Microsoft Outlook',
    'teams': 'Microsoft Teams',
    'excel': 'Microsoft Excel',
    'amazon': 'Amazon Seller Central',
    'walmart': 'Walmart Marketplace',
    'sage': 'Sage Intacct',
    'gemini': 'Google Gemini',
    'chatgpt': 'OpenAI',
    'gpt': 'OpenAI',
    'monday': 'Monday.com',
    'sheets': 'Google Sheets',
    'google sheet': 'Google Sheets',
    'docs': 'Google Docs',
    'drive': 'Google Drive',
    'woo': 'WooCommerce',
    'magento': 'Adobe Commerce',
    'bill': 'Bill.com',
    'facebook': 'Facebook Pages',
    'twitter': 'X',
    'entra': 'Microsoft Entra ID',
    'redshift': 'Amazon Redshift',
    'sap': 'SAP Business One',
    'adp': 'ADP Workforce Now'
}

const appsByName = new Map(apps.map(app => [app.name.toLowerCase(), app]))

export const findApp = (name: string): AppEntry | undefined => {
    const key = name.trim().toLowerCase()

    return appsByName.get(key) ?? (appAliases[key] ? appsByName.get(appAliases[key].toLowerCase()) : undefined)
}

const fallbackPalette = ['#5D2DE6', '#0F6CBD', '#1B8754', '#C2410C', '#7C3AED', '#0E7490', '#BE185D', '#4D7C0F', '#1D4ED8', '#9333EA']

const hashString = (value: string): number => {
    let hash = 0

    for (let index = 0; index < value.length; index += 1) {
        hash = (hash * 31 + value.charCodeAt(index)) | 0
    }

    return Math.abs(hash)
}

export const appColor = (name: string): string => findApp(name)?.color ?? fallbackPalette[hashString(name) % fallbackPalette.length]

export const appMonogram = (name: string): string => {
    const app = findApp(name)

    if (app?.mono) return app.mono

    const words = (app?.name ?? name).replace(/[^A-Za-z0-9 ]/g, ' ').split(/\s+/).filter(Boolean)

    if (words.length === 0) return '?'
    if (words.length === 1) return words[0].charAt(0).toUpperCase()

    return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase()
}

// Relative luminance check so light brand colors (yellows, limes) get dark monograms.
export const isLightColor = (hex: string): boolean => {
    const value = hex.replace('#', '')
    const full = value.length === 3 ? value.split('').map(part => part + part).join('') : value
    const red = parseInt(full.slice(0, 2), 16) / 255
    const green = parseInt(full.slice(2, 4), 16) / 255
    const blue = parseInt(full.slice(4, 6), 16) / 255
    const channel = (part: number) => (part <= 0.03928 ? part / 12.92 : Math.pow((part + 0.055) / 1.055, 2.4))
    const luminance = 0.2126 * channel(red) + 0.7152 * channel(green) + 0.0722 * channel(blue)

    return luminance > 0.45
}

const categoryLabels = new Map<string, string>()
const categoryGroupOf = new Map<string, string>()

appCategoryGroups.forEach(group => {
    categoryLabels.set(group.id, group.label)
    categoryGroupOf.set(group.id, group.id)

    group.children.forEach(child => {
        categoryLabels.set(child.id, child.label)
        categoryGroupOf.set(child.id, group.id)
    })
})

export const categoryLabel = (id: string): string => (id === 'all' ? 'All' : categoryLabels.get(id) ?? 'All')

export const isKnownCategory = (id: string): boolean => id === 'all' || categoryLabels.has(id)

export const groupOfCategory = (id: string): string | undefined => categoryGroupOf.get(id)

export const isGroupCategory = (id: string): boolean => appCategoryGroups.some(group => group.id === id)

// "Recently launched" sorts by the date an app joined the network. Apps without a date follow, newest additions to the list first.
export const sortApps = (list: Array<AppEntry>, sort: AppSort): Array<AppEntry> => {
    if (sort === 'alphabetical') return [...list].sort((first, second) => first.name.localeCompare(second.name))

    if (sort === 'recent') {
        return [...list].sort((first, second) => {
            if (first.added && second.added) return second.added.localeCompare(first.added)
            if (first.added) return -1
            if (second.added) return 1

            return apps.indexOf(second) - apps.indexOf(first)
        })
    }

    return list
}

export const filterApps = (list: Array<AppEntry>, category: string, query: string): Array<AppEntry> => {
    const search = query.trim().toLowerCase()

    return list.filter(app => {
        if (category !== 'all') {
            const matchesCategory = isGroupCategory(category)
                ? groupOfCategory(app.category) === category
                : app.category === category

            if (!matchesCategory) return false
        }

        if (!search) return true

        return app.name.toLowerCase().includes(search)
            || app.description.toLowerCase().includes(search)
            || categoryLabel(app.category).toLowerCase().includes(search)
    })
}
